
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { RootState } from '@/store';
import { updateAddressDetails, setAddressCompleted, setLoading } from '@/store/slices/profileSlice';
import { Address, BankDetails } from '@/types/interfaceType'
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'
import { ButtonWithLoader } from '../ui/buttonwithloader';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh'
];

interface AddressDetailsFormProps {
  onNext: () => void;
  updateProfile: (address?: Address, callback?: (res: any) => void) => Promise<void>;
}

const AddressDetailsForm: React.FC<AddressDetailsFormProps> = ({ onNext, updateProfile }) => {
  const dispatch = useDispatch();
  const profileState = useAppSelector(state => state.profile)

  const { addressDetails, loading } = useSelector((state: RootState) => state.profile);
  const [initialData] = useState(addressDetails);

  const { toast } = useToast();

  const [localData, setLocalData] = useState(addressDetails);


  const isAddressChanged = () => {
    return JSON.stringify(initialData) !== JSON.stringify(localData);
  };

  const handleInputChange = (field: string, value: string) => {
    let newValue= typeof value === 'string'? value?.trim():value
    const updatedData = { ...localData, [field]: newValue};
    setLocalData(updatedData);
    dispatch(updateAddressDetails({ [field]: newValue }));
  };

  const validateForm = (): boolean => {
    const required = ['address', 'locality', 'city', 'state', 'pincode'];

    const missing = required.filter(field => !localData[field as keyof typeof localData]);

    if (missing.length > 0) {
      toast({
        title: 'Error',
        description: `Please fill all required fields: ${missing.join(', ')}`,
        variant: 'destructive'
      });
      return false;
    }

    if (!/^\d{6}$/.test(localData.pincode)) {
      toast({ title: 'Error', description: 'Please enter a valid 6-digit pincode', variant: 'destructive' });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
  // 🚀 If nothing changed → skip API
    if (!isAddressChanged()) {
      dispatch(setAddressCompleted(true));
      onNext();
      return;
    }
    
    dispatch(setLoading(true));
    try {
      //  Simulate API call
      let payload = localData
      await updateProfile(payload, (profile: any) => {
        console.log("updateProfile response:", profile)
        if (profile?.success) {
          dispatch(setAddressCompleted(true));
          toast({ title: 'Success', description: 'Address details saved successfully!' });
          onNext();
        }
      })
      // await new Promise(resolve => setTimeout(resolve, 1000));

    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save address details', variant: 'destructive' });
    } finally {
      dispatch(setLoading(false));
    }
  };
  const isChanged = isAddressChanged();

  
  return (
    <div className="flex items-center justify-center w-96 sm:w-full p-4 sm:p-0">
      <Card className="w-full ">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Address Details</CardTitle>
          <CardDescription>Please provide your address information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="address">Address *</Label>
            <Input
              id="address"
              value={localData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="Enter your full address"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="locality">Locality *</Label>
              <Input
                id="locality"
                value={localData.locality}
                onChange={(e) => handleInputChange('locality', e.target.value)}
                placeholder="Enter locality"
                disabled={profileState.updateVendorProfile.isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={localData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Enter city"
                disabled={profileState.updateVendorProfile.isLoading}

              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Select value={localData.state || ""}
                onValueChange={(value) => handleInputChange('state', value)}
                disabled={profileState.updateVendorProfile.isLoading}

              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {INDIAN_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
                            
            <div className="space-y-2">
              <Label htmlFor="pincode">Pincode *</Label>
              <Input
                id="pincode"
                type="number"
                value={localData.pincode}
                onChange={(e) => handleInputChange('pincode', e.target.value)}
                placeholder="Enter 6-digit pincode"
                maxLength={6}
                disabled={profileState.updateVendorProfile.isLoading}
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <ButtonWithLoader
              loaderPosition='right'
              isLoading={profileState.updateVendorProfile.isLoading}
              onClick={handleSubmit}
              className="flex-1 bg-flup-brand hover:bg-flup-brand-press text-white"
              disabled={profileState.updateVendorProfile.isLoading}
            >
              {isChanged ? 
              
              profileState.updateVendorProfile.isLoading ? 'Saving...' : 'Save & Continue'
              : 'Continue'}

              {/* {profileState.updateVendorProfile.isLoading ? 'Saving...' : 'Save & Continue'} */}
            </ButtonWithLoader>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddressDetailsForm;
